// src/components/questionanswer/QnAJavaScript.jsx
import React, { useEffect, useMemo, useState } from "react";
import questionsJunior from "./qnaJSONdata/QnAJavascriptJunior.json";
import questionsSenior from "./qnaJSONdata/QnAJavascriptSenior.json";
import QnASideBarRight from "./QnASideBarRight";
import ScrollButton from "../../ScrollButton";

/**
 * QnAJavaScript — inline Q&A with LEFT TOC + right sidebar + keyword filter
 * - LEFT: sticky "On this page" + Sponsored ad
 * - MAIN: Junior & Senior sections + filter; header has Download PDF + Print
 * - RIGHT: existing sidebar (already includes its own Sponsored slot)
 */

const JS_PDF = "/pdfs/javascript-interview-questions.pdf"; // ensure file exists in /public/pdfs/
const normalize = (s = "") => s.toLowerCase();

// ============== AdSense helpers (same pattern as Java/SQL) ==============
const ADSENSE_CLIENT = "ca-pub-9033879555371063"; // <-- your AdSense client id

function useAdSense(clientId) {
  useEffect(() => {
    if (!clientId) return;
    const existing = document.querySelector("script[data-adsbygoogle]");
    if (!existing) {
      const s = document.createElement("script");
      s.setAttribute("data-adsbygoogle", "true");
      s.async = true;
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
      s.crossOrigin = "anonymous";
      document.head.appendChild(s);
    } else {
      try { (window.adsbygoogle = window.adsbygoogle || []).push({}); } catch {}
    }
  }, [clientId]);
}

function AdSlot({
  adSlot = "5201219465",
  format = "auto",
  fullWidthResponsive = "true",
  style = { display: "block", minHeight: 90 },
  className = "",
}) {
  useEffect(() => {
    try { (window.adsbygoogle = window.adsbygoogle || []).push({}); } catch {}
  }, []);
  return (
    <ins
      className={`adsbygoogle ${className}`}
      style={style}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={adSlot}
      data-ad-format={format}
      data-full-width-responsive={fullWidthResponsive}
    />
  );
}

// ============== UI bits ==============
function FilterBar({ value, onChange, total, shown }) {
  return (
    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
      <input
        className="form-control form-control-sm"
        style={{ maxWidth: 380 }}
        type="search"
        placeholder="Filter questions (e.g., scope, closures, async)…"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Filter questions"
      />
      <span className="text-muted small">Showing {shown} of {total}</span>
    </div>
  );
}

function QAInlineList({ items }) {
  return (
    <ol className="ps-3">
      {items.map((it, idx) => (
        <li key={it.id ?? idx} className="mb-3">
          <h5 className="mb-2">{it.question}</h5>
          <div className="text-body" style={{ whiteSpace: "pre-wrap" }}>{it.answer}</div>
        </li>
      ))}
    </ol>
  );
}

function OnThisPageLeft({ pdfHref }) {
  return (
    <div className="card shadow-sm border-0">
      <div className="card-body">
        <h2 className="h6 text-uppercase text-muted mb-2">On this page</h2>
        <nav className="nav flex-column small">
          <a className="nav-link px-0" href="#junior">Junior Q&amp;A</a>
          <a className="nav-link px-0" href="#senior">Senior Q&amp;A</a>
          <a
            className="nav-link px-0 mt-2 text-decoration-underline"
            href={pdfHref}
            download="TechVenture-JavaScript-Interview-Questions.pdf"
            aria-label="Download JavaScript interview questions PDF"
          >
            ⬇︎ Download PDF
          </a>
        </nav>
      </div>
    </div>
  );
}

function LeftSponsoredCard() {
  return (
    <div className="card shadow-sm border-0 mt-3">
      <div className="card-body">
        <h2 className="h6 text-uppercase text-muted mb-2">Sponsored</h2>
        <AdSlot adSlot="5201219465" />
        <div className="small text-muted mt-2">Ads help keep content free.</div>
      </div>
    </div>
  );
}

function TopActions({ pdfHref }) {
  return (
    <div className="d-flex gap-2">
      <a
        href={pdfHref}
        download="TechVenture-JavaScript-Interview-Questions.pdf"
        className="btn btn-outline-danger btn-sm"
        aria-label="Download JavaScript interview questions as PDF"
      >
        Download PDF
      </a>
      <button
        type="button"
        className="btn btn-outline-secondary btn-sm"
        onClick={() => window.print()}
        aria-label="Print this page"
      >
        Print
      </button>
    </div>
  );
}

// ============== Main ==============
export default function QnAJavaScript() {
  useAdSense(ADSENSE_CLIENT);

  const [query, setQuery] = useState("");

  const totalCount = (questionsJunior?.length || 0) + (questionsSenior?.length || 0);

  const filteredJunior = useMemo(() => {
    const qn = normalize(query);
    if (!qn) return questionsJunior;
    return questionsJunior.filter((x) =>
      normalize(x.question + " " + x.answer).includes(qn)
    );
  }, [query]);

  const filteredSenior = useMemo(() => {
    const qn = normalize(query);
    if (!qn) return questionsSenior;
    return questionsSenior.filter((x) =>
      normalize(x.question + " " + x.answer).includes(qn)
    );
  }, [query]);

  const shownCount = (filteredJunior?.length || 0) + (filteredSenior?.length || 0);

  return (
    <div className="container-fluid">
      <div className="row g-4 flex-xl-nowrap">
        {/* LEFT: On This Page + Sponsored (sticky) */}
        <aside className="d-none d-xl-block col-xl-2 pt-3">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <OnThisPageLeft pdfHref={JS_PDF} />
            <LeftSponsoredCard />
          </div>
        </aside>

        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-7 pt-3" role="main">
          <header className="mb-2 d-flex justify-content-between align-items-start">
            <div>
              <h1 className="h2 mb-1">JavaScript Interview Questions</h1>
              <p className="text-muted mb-0">
                Curated questions for junior and senior roles. Use the filter to jump to what you need.
              </p>
            </div>
            <TopActions pdfHref={JS_PDF} />
          </header>

          <FilterBar value={query} onChange={setQuery} total={totalCount} shown={shownCount} />

          {/* Junior section */}
          <section id="junior" className="pt-2">
            <h2 className="h4 mt-3">JavaScript Interview Questions for Junior Developers</h2>
            <p className="text-body-secondary">
              JavaScript powers interactive, dynamic experiences on the web and beyond. These questions cover fundamentals—types,
              scope, closures, DOM APIs, async patterns, and browser quirks.
            </p>
            <QAInlineList items={filteredJunior} />
          </section>

          {/* Optional in-article ad (leave commented to keep ads only in Sponsored sections) */}
          {/*
          <div className="my-3 text-center">
            <AdSlot adSlot="5201219465" style={{ display: "block", minHeight: 90, textAlign: "center" }} />
          </div>
          */}

          {/* Senior section */}
          <section id="senior" className="pt-2">
            <h2 className="h4 mt-4">JavaScript Interview Questions for Senior Developers</h2>
            <p className="text-body-secondary">
              Advanced topics like event loop internals, performance, architecture, testing, security, and Node.js/SSR patterns
              typically asked for senior roles.
            </p>
            <QAInlineList items={filteredSenior} />
          </section>

          <footer className="border-top mt-4 pt-3 small text-muted">
            Tip: Practice aloud. Explaining an answer reveals gaps faster than silent reading.
          </footer>

          <ScrollButton />
        </main>

        {/* RIGHT: existing sidebar */}
        <aside className="col-12 col-lg-4 col-xl-3 pt-3 border-start">
          <QnASideBarRight />
        </aside>
      </div>
    </div>
  );
}
